import { computed, ref, watch, type Ref } from 'vue';
import type { TComplianceSummary } from '@/_core/entities/report/models/types';
import { useLocalStorageCache } from './useLocalStorageCache';

const STORAGE_KEY = 'compliance-filter-label-cache';

export function useComplianceFilter(options: {
  summary: Ref<TComplianceSummary | undefined>;
  selectedRequirementId: Ref<string | undefined>;
  selectedItemId: Ref<string | undefined>;
  onClear: () => void;
}) {
  const { summary, selectedRequirementId, selectedItemId, onClear } = options;

  const search = ref('');
  const requirements = computed(() => summary.value?.requirements ?? []);
  const cache = useLocalStorageCache<Record<string, string>>(STORAGE_KEY, {});

  function currentSelectionKey(): string | null {
    if (selectedItemId.value) return `item:${selectedItemId.value}`;
    if (selectedRequirementId.value) return `req:${selectedRequirementId.value}`;
    return null;
  }

  function resolveLabelFromSummary(): string {
    if (selectedItemId.value) {
      for (const req of requirements.value) {
        const item = req.items.find((i) => i.id === selectedItemId.value);
        if (item) return item.itemId;
      }
    }
    if (selectedRequirementId.value) {
      const req = requirements.value.find((r) => r.id === selectedRequirementId.value);
      if (req) return req.name;
    }
    return '';
  }

  const selectedLabel = computed(() => {
    const key = currentSelectionKey();
    if (!key) return '';
    const resolved = resolveLabelFromSummary();
    if (resolved) return resolved;
    return cache.value[key] ?? 'Filter applied';
  });

  watch(
    [requirements, selectedItemId, selectedRequirementId],
    () => {
      const key = currentSelectionKey();
      if (!key) return;
      const resolved = resolveLabelFromSummary();
      if (resolved && cache.value[key] !== resolved) {
        cache.value = { ...cache.value, [key]: resolved };
      }
    },
    { immediate: true }
  );

  watch(
    selectedLabel,
    (label) => {
      search.value = label;
    },
    { immediate: true }
  );

  watch(search, (value) => {
    if (selectedLabel.value && value !== selectedLabel.value) {
      onClear();
    }
  });

  const filteredGroups = computed(() => {
    const q = search.value.trim().toLowerCase();
    if (!q || q === selectedLabel.value.toLowerCase()) return requirements.value;
    return requirements.value
      .map((req) => {
        const reqMatches = req.name.toLowerCase().includes(q);
        const items = req.items.filter((it) => it.itemId.toLowerCase().includes(q));
        if (reqMatches) return req;
        if (items.length > 0) return { ...req, items };
        return null;
      })
      .filter((r): r is NonNullable<typeof r> => r !== null);
  });

  const selectedCount = computed(() =>
    selectedItemId.value || selectedRequirementId.value ? 1 : 0
  );

  return {
    search,
    selectedLabel,
    filteredGroups,
    selectedCount,
  };
}
